import { Link } from "react-router-dom";
import { Hourglass, Scales, FileText, Stamp, ArrowRight } from "@phosphor-icons/react";
import TendancesSection from "../components/TendancesSection";
import CMSTribune from "../components/CMSTribune";

const HERO = "https://images.unsplash.com/photo-1774369914913-c46c0dd2fe3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxqdWRpY2lhbCUyMGRvY3VtZW50fGVufDB8fHx8MTc3ODMzNzQyMXww&ixlib=rb-4.1.0&q=85";
const HOURGLASS_IMG = "https://images.unsplash.com/photo-1660728800047-c172f5a57014?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxob3VyZ2xhc3MlMjBzYW5kJTIwbWFjcm98ZW58MHx8fHwxNzc4MzM3NDEwfDA&ixlib=rb-4.1.0&q=85";
const GAVEL_IMG = "https://images.unsplash.com/photo-1767972159871-b9f5d320be2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxqdWRpY2lhbCUyMGRvY3VtZW50fGVufDB8fHx8MTc3ODMzNzQyMXww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="bg-[var(--paper)]">
      {/* HERO */}
      <section className="relative border-b border-black overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-30 grayscale" />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--paper)]/40 via-[var(--paper)]/70 to-[var(--paper)]" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8 pt-24 pb-32">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-2 border-r border-black/20 pr-4 hidden lg:block">
              <div className="label-mono">Dossier</div>
              <div className="font-mono text-xs text-[var(--muted)] mt-2">N° 001 / 2026</div>
              <div className="font-mono text-xs text-[var(--muted)] mt-1">Confidentiel</div>
              <div className="font-mono text-xs text-[var(--muted)] mt-1">FR · Mauritanie</div>
            </div>
            <div className="col-span-12 lg:col-span-10 fade-up">
              <div className="label-mono mb-6">Théorie Vitaliste des Finances Publiques</div>
              <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-medium leading-[0.95] tracking-tight">
                Réécrire l'argent<br/>en <span className="italic text-[var(--signal)]">temps de vie</span>.
              </h1>
              <p className="mt-8 max-w-2xl text-lg sm:text-xl text-[var(--muted)] leading-relaxed font-light">
                <strong className="text-[var(--ink)]">VITA-LEX ANALYTICA</strong> convertit chaque chiffre budgétaire en jours,
                semaines et années de vie humaine — pour révéler la confiscation temporelle cachée
                derrière les lois de finances et la dette publique.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/auth" data-testid="hero-cta-start" className="bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-6 py-4 label-mono flex items-center gap-2">
                  Ouvrir mon dossier vitaliste <ArrowRight size={16} weight="bold" />
                </Link>
                <Link to="/auth" data-testid="hero-cta-login" className="border border-black hover:bg-black hover:text-[var(--paper)] px-6 py-4 label-mono">
                  Connexion citoyen
                </Link>
                <Link to="/glossaire-public" data-testid="hero-cta-glossaire" className="px-6 py-4 label-mono text-[var(--muted)] hover:text-[var(--signal)] underline-offset-4 hover:underline">
                  Glossaire libre →
                </Link>
              </div>

              <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-px bg-black/15 border border-black/15 max-w-3xl">
                {[
                  ["11", "Formules vitalistes"],
                  ["V1—V7", "Pression fiscale"],
                  ["D1—D5", "Dette & hypothèque"],
                  ["DVC + RRC", "Actes juridiques"],
                ].map(([k,v]) => (
                  <div key={k} className="bg-[var(--paper)] p-4">
                    <div className="font-serif text-2xl font-medium">{k}</div>
                    <div className="label-mono mt-1">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stamp */}
        <div className="absolute right-8 lg:right-32 top-32 hidden md:block stamp-rotate">
          <div className="border-2 border-[var(--signal)] text-[var(--signal)] px-5 py-3 font-mono text-xs tracking-widest">
            <div className="font-bold">CONFIDENTIEL</div>
            <div className="text-[10px] opacity-80">PIÈCE COMPTABLE</div>
          </div>
        </div>
      </section>

      {/* MANIFESTE */}
      <section className="border-b border-black/15">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-3">
            <div className="label-mono">§ 01 · Manifeste</div>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-medium tracking-tight">Le paradigme vitaliste</h2>
          </div>
          <div className="col-span-12 lg:col-span-6 text-lg leading-relaxed font-light">
            <p>L'argent n'est qu'une <em className="italic">représentation conventionnelle</em> du temps que nous avons cédé.
            Quand l'État prélève cent unités, il ne prélève pas une chose : il prélève un fragment de notre existence,
            mesurable, datable, irrécupérable.</p>
            <p className="mt-6">Chaque ligne d'un budget public est, en réalité, un acte de redistribution de jours de vie.
            Toute dette publique est une <strong>hypothèque vitaliste collective</strong> qui engage
            les générations à venir.</p>
            <p className="mt-6 text-[var(--signal)]"><strong>VITA-LEX ANALYTICA</strong> rend ce calcul visible, irréfutable, et juridiquement opposable.</p>
          </div>
          <div className="col-span-12 lg:col-span-3 lg:border-l lg:border-black/15 lg:pl-6">
            <img src={HOURGLASS_IMG} alt="" className="w-full aspect-[4/5] object-cover grayscale" />
            <div className="label-mono mt-3">Pièce 01 — Sablier macro</div>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section className="border-b border-black/15 bg-[var(--paper-2)]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="label-mono">§ 02 · Modules opérationnels</div>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-medium tracking-tight">Quatre instruments,<br/>une seule vérité.</h2>
            </div>
            <div className="hidden md:block font-mono text-xs text-[var(--muted)]">VLA / 2026.02</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/15 border border-black/15">
            {[
              { icon: Hourglass, k: "VITA-ENGINE", t: "Moteur de calcul vitaliste", d: "11 formules opérationnelles : V1 à V7 (pression fiscale), D1 à D5 (dette). Conversion automatique en jours sacrifiés et Score de Retour Vitaliste pondéré." },
              { icon: FileText, k: "VITA-PARSER", t: "Parseur intelligent", d: "Importez un PDF (loi de finances, registre de la dette, bilan). L'IA extrait les agrégats et les normalise pour le moteur." },
              { icon: Stamp, k: "VITA-WRITER", t: "Rédaction juridique", d: "Génération de la Déclaration Vitaliste Citoyenne (DVC) et de la Requête de Reddition de Comptes (RRC) en français formel." },
              { icon: Scales, k: "VITA-JUDGE", t: "Simulateur de jugement", d: "Qualification automatique du déficit vitaliste, du satisfaisant au crime contre la vie collective (Jvita ≥ 365)." },
            ].map((m, i) => (
              <div key={m.k} className="bg-[var(--paper)] p-8 fade-up" style={{ animationDelay: `${i*0.08}s` }}>
                <div className="flex items-start gap-5">
                  <div className="border border-black p-3">
                    <m.icon size={28} weight="duotone" />
                  </div>
                  <div className="flex-1">
                    <div className="label-mono">{m.k}</div>
                    <h3 className="mt-2 font-serif text-2xl font-medium">{m.t}</h3>
                    <p className="mt-3 text-[var(--muted)] leading-relaxed">{m.d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TENDANCES 2026 — vitrine éditoriale agrégée */}
      <TendancesSection />

      {/* CITATION */}
      <section className="border-b border-black bg-[var(--ink)] text-[var(--paper)]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-4">
            <img src={GAVEL_IMG} alt="" className="w-full aspect-square object-cover grayscale opacity-80" />
          </div>
          <div className="col-span-12 lg:col-span-8 lg:pl-8 flex flex-col justify-center">
            <div className="label-mono text-[var(--signal)]">§ 03 · Cas didactique « Mamadou »</div>
            <blockquote className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight font-light">
              « Ce prêt de 100 millions UM représente l'aliénation
              de <span className="text-[var(--signal)] italic">5 jours de vie</span> pour chaque nouveau-né.
              C'est ainsi que la dette se paie : <em>en temps</em>. »
            </blockquote>
            <div className="mt-8 label-mono opacity-70">Extrait — Rapport DEBT-VITA, p. 47</div>
          </div>
        </div>
      </section>

      {/* MANUEL PUBLIC — encart libre de droits */}
      <section className="border-y border-black/15 bg-[var(--paper-2)]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="label-mono">§ Référence publique · Accès libre</div>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-medium leading-tight">
              Manuel pratique citoyen — <span className="text-[var(--signal)]">libre de droits</span>
            </h2>
            <p className="mt-4 text-[var(--muted)] leading-relaxed max-w-2xl">
              Trente pages pour parler couramment des finances publiques avec une grammaire vitaliste. Glossaire de 37 termes, parcours en 5 étapes, 10 prompts modèles, 3 cas d'usage. Citable dans vos articles, mémoires et plaidoyers.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/public/apprentissage/manuel.pdf`}
                 download="vita-lex-manuel-pratique.pdf"
                 data-testid="landing-public-manuel"
                 className="bg-[var(--signal)] text-[var(--paper)] hover:bg-[var(--signal-hover)] px-6 py-3 label-mono inline-flex items-center gap-2">
                Télécharger le manuel (PDF) ↓
              </a>
              <Link to="/glossaire-public" data-testid="landing-public-glossary" className="border border-black hover:bg-black hover:text-[var(--paper)] px-6 py-3 label-mono inline-flex items-center gap-2">
                Consulter le glossaire en ligne →
              </Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="border-2 border-[var(--signal)] bg-[var(--paper)] p-6 relative overflow-hidden">
              <div className="absolute top-3 right-3 font-mono text-[10px] tracking-[0.18em] text-[var(--signal)]">§ 00</div>
              <div className="font-mono text-[10px] tracking-[0.18em] text-[var(--muted)] uppercase">Sommaire</div>
              <div className="mt-3 space-y-1.5 text-sm font-serif">
                <div className="flex justify-between border-b border-dashed border-black/15 py-1">§01 · Onboarding 5 étapes <span className="text-[var(--muted)]">p.04</span></div>
                <div className="flex justify-between border-b border-dashed border-black/15 py-1">§02 · Glossaire 37 termes <span className="text-[var(--muted)]">p.09</span></div>
                <div className="flex justify-between border-b border-dashed border-black/15 py-1">§03 · 10 prompts modèles <span className="text-[var(--muted)]">p.18</span></div>
                <div className="flex justify-between border-b border-dashed border-black/15 py-1">§04 · 3 cas d'usage <span className="text-[var(--muted)]">p.24</span></div>
                <div className="flex justify-between py-1">§05 · Annexe technique <span className="text-[var(--muted)]">p.28</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <CMSTribune />
      <section>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-24 text-center">
          <div className="label-mono">§ 04 · Souveraineté vitaliste</div>
          <h2 className="mt-4 font-serif text-4xl sm:text-6xl font-medium tracking-tight max-w-3xl mx-auto leading-[1.05]">
            Mesurez votre vie.<br/>Réclamez vos jours.
          </h2>
          <p className="mt-6 text-lg text-[var(--muted)] max-w-2xl mx-auto">
            Activez votre dossier citoyen, importez vos chiffres, et générez vos actes juridiques en moins de cinq minutes.
          </p>
          <Link to="/auth" data-testid="footer-cta" className="inline-flex mt-10 bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-8 py-4 label-mono items-center gap-2">
            Ouvrir mon dossier <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-black/15 py-8 text-center label-mono">
        VITA-LEX ANALYTICA · 2026 · Démocratie budgétaire directe
        <span className="opacity-30 mx-3">·</span>
        <Link to="/admin/login" className="opacity-50 hover:opacity-100 hover:text-[var(--signal)]" data-testid="footer-admin-link">Console admin</Link>
      </footer>
    </div>
  );
}
